const WALLET_CLIENT_SESSION_KEY = 'shop_wallet_client';

/** 支付页深链接可选的 4 种钱包（与 Payment / TgPayment 等 payList 一致） */
export const DEEPLINK_WALLET_IDS = Object.freeze([
  'imtoken',
  'tokenpocket',
  'bitget',
  'tronlink',
]);

/** 支付页勾选钱包后写入 session，并随 DApp URL ?wallet= 传给 WalletPay */
export function persistWalletClientChoice(walletId) {
  const normalized = normalizeWalletClientId(walletId);
  if (!normalized) return;
  try {
    sessionStorage.setItem(WALLET_CLIENT_SESSION_KEY, normalized);
  } catch {
    /* ignore */
  }
}

function readPersistedWalletClient() {
  try {
    return sessionStorage.getItem(WALLET_CLIENT_SESSION_KEY)?.trim().toLowerCase() || '';
  } catch {
    return '';
  }
}

export function normalizeWalletClientId(raw) {
  const id = String(raw ?? '').trim().toLowerCase();
  return DEEPLINK_WALLET_IDS.includes(id) ? id : '';
}

function detectPlatform(ua) {
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'web';
}

/** 将 ?wallet=imtoken 追加到 DApp URL（钱包 WebView 内 sessionStorage 可能为空） */
export function appendWalletClientQuery(url, walletId) {
  const wallet = normalizeWalletClientId(walletId);
  if (!wallet || !url) return url;

  const hashIdx = url.indexOf('#');
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const qIdx = withoutHash.indexOf('?');
  const pathPart = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  const query = qIdx >= 0 ? withoutHash.slice(qIdx + 1) : '';

  const params = new URLSearchParams(query);
  params.set('wallet', wallet);
  const nextQuery = params.toString();
  return `${pathPart}?${nextQuery}${hash}`;
}

function readWalletFromRoute(route) {
  const fromRoute = normalizeWalletClientId(route?.query?.wallet);
  if (fromRoute) return fromRoute;

  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return normalizeWalletClientId(params.get('wallet'));
}

/** WalletPay / WalletLink 进入时，从 URL 同步用户勾选的钱包 */
export function syncWalletClientFromRoute(route) {
  const wallet = readWalletFromRoute(route);
  if (wallet) {
    persistWalletClientChoice(wallet);
  }
}

export function isTokenPocketWallet(route) {
  if (typeof window === 'undefined') return false;
  const wallet =
    readWalletFromRoute(route) || normalizeWalletClientId(readPersistedWalletClient());
  return wallet === 'tokenpocket';
}

/** TokenPocket Android：直接走 buildRawAppTransaction，不走 raw 优先 */
export function isTokenPocketAndroid(route) {
  if (!isTokenPocketWallet(route)) return false;
  return detectPlatform(navigator.userAgent || '') === 'android';
}

/**
 * TronLink Android 仍走 buildRawTransaction 优先的双路径逻辑，iOS 不变
 */
export function isTronLinkAndroid(route) {
  if (typeof window === 'undefined') return false;
  const wallet =
    readWalletFromRoute(route) || normalizeWalletClientId(readPersistedWalletClient());
  if (wallet !== 'tronlink') return false;
  return detectPlatform(navigator.userAgent || '') === 'android';
}

/**
 * 是否优先 buildRawTransaction：
 * - TokenPocket iOS：是（失败再降级 AppTransaction）
 * - TokenPocket Android：否（直接 AppTransaction）
 * - TronLink Android：是
 */
export function shouldUseRawFirst(route) {
  if (isTokenPocketAndroid(route)) return false;
  return isTokenPocketWallet(route) || isTronLinkAndroid(route);
}

/**
 * 访问播报用：仅展示支付页勾选的 4 种深链接钱包，格式 imtoken|android
 * @param {import('vue-router').RouteLocationNormalizedLoaded|object} [route]
 */
export function detectWalletClient(route) {
  if (typeof window === 'undefined') return 'unknown|web';

  const platform = detectPlatform(navigator.userAgent || '');
  const wallet =
    readWalletFromRoute(route) || normalizeWalletClientId(readPersistedWalletClient());

  if (wallet) {
    persistWalletClientChoice(wallet);
    return `${wallet}|${platform}`;
  }

  return `unknown|${platform}`;
}
